import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = "/api";

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const paymentLabel = (method) => {
  const labels = {
    cash: "Efectivo",
    card: "Tarjeta",
    transfer: "Transferencia",
  };

  return labels[method] || method;
};

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

function SaleReturnModal({
  sale,
  mode,
  onClose,
  onCompleted,
}) {
  const isDetail = mode === "detail";
  const isCancellation = mode === "cancel";

  const availableItems = sale.items.filter(
    (item) => Number(item.available_to_return) > 0
  );

  const [quantities, setQuantities] = useState({});
  const [refundMethod, setRefundMethod] = useState(
    sale.payments?.[0]?.payment_method || "cash"
  );
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const initialQuantities = {};

    sale.items.forEach((item) => {
      initialQuantities[item.id] = isCancellation
        ? Number(item.available_to_return)
        : 0;
    });

    setQuantities(initialQuantities);
  }, [sale, isCancellation]);

  const refundTotal = useMemo(
    () =>
      sale.items.reduce((total, item) => {
        const quantity = Number(quantities[item.id] || 0);

        return total + quantity * Number(item.unit_price);
      }, 0),
    [sale.items, quantities]
  );

  const paymentMethods = [
    ...new Set(
      (sale.payments || []).map((payment) => payment.payment_method)
    ),
  ];

  const updateQuantity = (item, value) => {
    const available = Number(item.available_to_return);
    let quantity = Number(value || 0);

    if (quantity < 0) quantity = 0;
    if (quantity > available) quantity = available;

    setQuantities((current) => ({
      ...current,
      [item.id]: quantity,
    }));
  };

  const submitReturn = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const items = sale.items
      .map((item) => ({
        sale_item_id: item.id,
        quantity: Number(quantities[item.id] || 0),
      }))
      .filter((item) => item.quantity > 0);

    if (items.length === 0) {
      setErrorMessage("Selecciona al menos un producto.");
      return;
    }

    if (reason.trim().length < 3) {
      setErrorMessage(
        "Escribe un motivo de al menos 3 caracteres."
      );
      return;
    }

    setSaving(true);

    try {
      const response = await axios.post(
        `${API_URL}/sales/${sale.id}/returns`,
        {
          items,
          refund_method: refundMethod,
          reason: reason.trim(),
        },
        {
          headers: getHeaders(),
        }
      );

      onCompleted(response.data.message);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sale-modal-backdrop">
      <section className="sale-modal">
        <button
          type="button"
          className="sale-modal-close"
          onClick={onClose}
          disabled={saving}
        >
          ×
        </button>

        <div className="sale-modal-header">
          <div>
            <span>
              Venta {sale.document_number || `#${sale.id}`}
            </span>

            <h2>
              {isDetail
                ? "Detalle de venta"
                : isCancellation
                  ? "Anular venta"
                  : "Registrar reintegro"}
            </h2>
          </div>

          <span className={`sale-status sale-status-${sale.status}`}>
            {sale.status === "cancelled"
              ? "Anulada"
              : sale.status === "partially_refunded"
                ? "Reintegro parcial"
                : "Completada"}
          </span>
        </div>

        <div className="sale-detail-data">
          <div>
            <span>Cliente</span>
            <strong>{sale.customer_name || "Cliente general"}</strong>
          </div>

          <div>
            <span>Fecha</span>
            <strong>
              {new Date(sale.created_at).toLocaleString("es-CO")}
            </strong>
          </div>

          <div>
            <span>Total venta</span>
            <strong>{money(sale.total)}</strong>
          </div>
        </div>

        {isDetail ? (
          <>
            <h3 className="sale-modal-subtitle">Productos vendidos</h3>

            <div className="sale-return-items">
              {sale.items.map((item) => (
                <div className="sale-return-item" key={item.id}>
                  <div>
                    <strong>{item.product_name}</strong>
                    <small>
                      {item.sku} · {money(item.unit_price)}
                    </small>
                  </div>

                  <div className="sale-item-quantities">
                    <span>Vendido: {item.quantity}</span>
                    <span>Reintegrado: {item.returned_quantity}</span>
                    <b>Disponible: {item.available_to_return}</b>
                  </div>
                </div>
              ))}
            </div>

            {sale.returns?.length > 0 && (
              <>
                <h3 className="sale-modal-subtitle">
                  Historial de reintegros
                </h3>

                <div className="sale-returns-history">
                  {sale.returns.map((saleReturn) => (
                    <div key={saleReturn.id}>
                      <strong>{money(saleReturn.total)}</strong>
                      <span>
                        {paymentLabel(saleReturn.refund_method)} ·{" "}
                        {saleReturn.reason}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <button
              type="button"
              className="btn-sale-modal-secondary"
              onClick={onClose}
            >
              Cerrar
            </button>
          </>
        ) : (
          <form onSubmit={submitReturn}>
            <p className="sale-return-help">
              {isCancellation
                ? "Se reintegrarán todas las unidades pendientes de esta venta."
                : "Indica las cantidades que el cliente está devolviendo."}
            </p>

            <div className="sale-return-items">
              {availableItems.length === 0 ? (
                <p className="sale-no-available-items">
                  No hay productos disponibles para reintegrar.
                </p>
              ) : (
                availableItems.map((item) => (
                  <div className="sale-return-item" key={item.id}>
                    <div>
                      <strong>{item.product_name}</strong>
                      <small>
                        {item.sku} · {money(item.unit_price)}
                      </small>
                      <small>
                        Disponible para reintegro: {item.available_to_return}
                      </small>
                    </div>

                    <input
                      type="number"
                      min="0"
                      max={item.available_to_return}
                      value={quantities[item.id] || 0}
                      onChange={(event) =>
                        updateQuantity(item, event.target.value)
                      }
                      disabled={isCancellation}
                    />
                  </div>
                ))
              )}
            </div>

            <div className="sale-return-form-grid">
              <div>
                <label htmlFor="refund_method">
                  Método de reintegro
                </label>

                <select
                  id="refund_method"
                  value={refundMethod}
                  onChange={(event) =>
                    setRefundMethod(event.target.value)
                  }
                >
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {paymentLabel(method)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label>Total a reintegrar</label>

                <div className="sale-refund-total">
                  {money(refundTotal)}
                </div>
              </div>
            </div>

            <div className="sale-return-reason">
              <label htmlFor="return_reason">Motivo</label>

              <textarea
                id="return_reason"
                rows="3"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Ejemplo: Producto en mal estado"
                required
              />
            </div>

            {errorMessage && (
              <p className="sale-return-error">{errorMessage}</p>
            )}

            <div className="sale-modal-actions">
              <button
                type="button"
                className="btn-sale-modal-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className={
                  isCancellation
                    ? "btn-confirm-cancellation"
                    : "btn-confirm-return"
                }
                disabled={saving || availableItems.length === 0}
              >
                {saving
                  ? "Procesando..."
                  : isCancellation
                    ? "Confirmar anulación"
                    : "Confirmar reintegro"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

export default SaleReturnModal;